import React, { useEffect, useState } from 'react';
import Modal from '../../../../../Common/Components/Modal';
import Textbox from '../../../../../Common/Components/Textbox';
import Checkbox from '../../../../../Common/Components/Checkbox';
import SelectBox from '../../../../../Common/Components/SelectBox';
import { SelectBoxItemType } from '../../../../../types/SelectBoxPropsType';
import { AdminEditUserProps, EditUserStateProp, EditUserPostProp } from '../../../../../types/AdminEditUserProps';
import Utils from '../../../../../utils/Utils';
import { putRequest } from '../../../../../utils/fetch';
import { useAppContext } from '../../../../../utils/AppContext';
import formValidation from '../../../../../utils/form/formValidation';

function AdminSidebarEditUser(props: AdminEditUserProps): JSX.Element {
  const { modalOpen, updateCustomers, editUser } = props;
  const { addNotification } = useAppContext();
  const [editSelectedUser, setEditSelectedUser] = useState<EditUserStateProp>({
    username: editUser?.email || '',
    partner: editUser?.partner || '',
    account: editUser?.account || '',
    roles: { title: editUser?.rolesTitle || editUser?.roles, key: editUser?.roles },
    firstname: editUser?.name.split(' ')[0] || '',
    lastname: editUser?.name.split(' ')[1] || '',
    mobile: editUser?.phone || '1',
    mfa: (editUser?.mfa === 'Enabled') || false,
    mobileError: false,
    mobileErrorMessage: '',
    mfaError: false,
    mfaErrorMessage: '',
  });

  const selectBoxWidth = 300;

  const [rolesFilter, setRolesFilter] = useState([{ title: 'None', key: '0' }]);

  const mfaErrorMessage = 'Enter mobile number for two-factor authentication.';

  const checkPhoneValidation = (newName?: string) => {
    if (newName === '' || !newName?.startsWith('1')) {
      // eslint-disable-next-line no-param-reassign
      newName = `1${newName}`;
    }
    const mobile = newName !== undefined ? newName : editSelectedUser.mobile;
    const validation = formValidation(
      mobile,
      { phone: true },
    );

    if (editSelectedUser.mfa && mobile === '1') {
      setEditSelectedUser((oldValues) => ({
        ...oldValues,
        mobile,
        mfaError: true,
        mfaErrorMessage,
        mobileError: false,
        mobileErrorMessage: '',
      }));
    } else {
      setEditSelectedUser((oldValues) => ({
        ...oldValues,
        mobile,
        mfaError: false,
        mfaErrorMessage: '',
        mobileError: validation.hasError,
        mobileErrorMessage: validation.errorMsg,
      }));
    }
  };
  const checkMobileSelectedValidation = (checked?: boolean) => {
    const validationError = editSelectedUser.mobile === '' || editSelectedUser.mobile === '1';
    const validationErrorMsg = validationError ? mfaErrorMessage : '';
    const mfa = !editSelectedUser.mfa;

    if (checked) {
      setEditSelectedUser((oldValues) => ({
        ...oldValues,
        mfa,
        mfaError: validationError,
        mfaErrorMessage: validationErrorMsg,
      }));
    } else {
      setEditSelectedUser((oldValues) => ({
        ...oldValues,
        mfa,
        mfaError: false,
        mfaErrorMessage: '',
      }));
    }
  };

  useEffect(() => {
    if (editUser.roledetails === 'sensityroles') {
      setRolesFilter(Utils.getUserRolesByType('sensity'));
    } else if (editUser.roledetails === 'partnerroles') {
      setRolesFilter(Utils.getUserRolesByType('partner'));
    } else {
      setRolesFilter(Utils.getUserRolesByType('account'));
    }
  }, [
    editUser,
  ]);

  return (
    <div className="user-interactions edit-user">
      <Modal
        className="modal"
        title="Edit user"
        setModalOpen={modalOpen}
        primaryButtonLabel="Save"
        primaryButtonAction={async () => {
          const currentUser = editSelectedUser;
          const role = currentUser.roles.key.toUpperCase();

          if (currentUser.mfaError || currentUser.mobileError) {
            return;
          }

          try {
            const phone = editSelectedUser.mobile === '' || editSelectedUser.mobile === '1' ? null : editSelectedUser.mobile;
            const postBody: EditUserPostProp = {
              email: editSelectedUser.username,
              name: `${editSelectedUser.firstname} ${editSelectedUser.lastname}`.trimEnd(),
              phone,
              mfa: editSelectedUser.mfa,
            };
            const result = await putRequest(
              `/organizations/${editUser.accountid}/users/${editUser.userid}?targetRole=${role}`,
              postBody,
            );

            if (!result.error) {
              addNotification({ type: 'info', message: 'Your "Edit user" operation is completed.' });
              if (updateCustomers) {
                updateCustomers();
              }
            } else {
              addNotification({ type: 'error', message: `Your "Edit user" operation has failed: ${result.error}` });
            }
          } catch (e) {
            addNotification({ type: 'error', message: 'Your "Edit user" operation has failed.' });
          }
          modalOpen(false);
        }}
        secondaryButtonLabel="Cancel"
        width="710"
        secondaryButtonAction={() => modalOpen(false)}
      >
        <div className="modal-divided">
          <div className="modal-divided__leftcontent">
            <div className="modal-divided__content-data">
              <Textbox
                label="Partner"
                name="Partner"
                value={editSelectedUser?.partner || ''}
                placeholder={editSelectedUser?.partner || ''}
                disabled
                gridTemplateColumns={`auto ${selectBoxWidth}px`}
              />
              <Textbox
                label="Account"
                name="Account"
                value={editSelectedUser?.account || ''}
                placeholder={editSelectedUser?.account || ''}
                disabled
                gridTemplateColumns={`auto ${selectBoxWidth}px`}
              />
              <SelectBox
                label="Role"
                onClick={(item: SelectBoxItemType) =>
                  setEditSelectedUser((oldValues) => ({ ...oldValues, roles: item }))}
                selectedItemKey={editSelectedUser?.roles?.key || ''}
                list={Utils.arrayToSelectbox(rolesFilter || [], 'key', 'title')}
                title={editSelectedUser.roles.title}
                type="light"
                listWidth={selectBoxWidth}
                isRequired
              />
              <Textbox
                label="Username (Email)"
                name="Email"
                value={editSelectedUser?.username}
                gridTemplateColumns={`auto ${selectBoxWidth}px`}
                disabled
              />
            </div>
          </div>
          <div className="modal-divided__rightcontent">
            <Textbox
              label="First name"
              name="FirstName"
              value={editSelectedUser.firstname}
              placeholder="Enter"
              onChange={(event) => {
                const firstname = event.target.value;
                setEditSelectedUser((oldValues) => ({ ...oldValues, firstname }));
              }}
              gridTemplateColumns={`auto ${selectBoxWidth}px`}
            />
            <Textbox
              label="Last name"
              name="LastName"
              value={editSelectedUser.lastname}
              placeholder="Enter"
              onChange={(event) => {
                const lastname = event.target.value;
                setEditSelectedUser((oldValues) => ({ ...oldValues, lastname }));
              }}
              gridTemplateColumns={`auto ${selectBoxWidth}px`}
            />
            <Textbox
              label="Mobile (SMS)"
              name="Mobile"
              value={editSelectedUser.mobile}
              placeholder="Enter"
              onChange={(event) => {
                const mobile = event.target.value;
                checkPhoneValidation(mobile);
              }}
              error={editSelectedUser.mfaError || editSelectedUser.mobileError}
              errorMessage={editSelectedUser.mfaErrorMessage || editSelectedUser.mobileErrorMessage}
              gridTemplateColumns={`auto ${selectBoxWidth}px`}
            />
            <div className="users-mfa">
              <span>Multi-factor authentication (MFA)</span>
              <div className="mfa-content">
                <Checkbox
                  checked={editSelectedUser.mfa}
                  onClick={(checked) => {
                    checkMobileSelectedValidation(checked);
                  }}
                />
                <span>Required</span>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default AdminSidebarEditUser;
